import React, { forwardRef, useRef } from "react";

import cn from "classnames";

import { useIsomorphicLayoutEffect } from "../../../../../../hooks";
import { calcScrollHeightByElementsCount } from "../../../../../../utils/dom/dom";
import { useDropdownStore } from "../../../../store/store";
import {
    DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ATRIBUTE,
    DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ID,
} from "../../../Item/DropdownItem";

import type { DropdownProps } from "./types";

import classes from "./MenuBase.styl";

/**
 * Базовый компонент, выпадающий список для выбора опций
 *
 * <a href="https://www.figma.com/design/SZCPsHnuqBIWlYSyfmq0j1/%F0%9F%A7%A9-Sheets-%26-Modules?node-id=287-96158&t=C79blbKc3GOumKo6-1">Спека</a>
 */
export const Base = forwardRef<HTMLDivElement, DropdownProps>(
    (
        {
            role,
            className,
            width = "240",
            maxVisibleCells = 6,
            view = "primary",
            customVarsStyles,
            children,
            customColorsStyles,
            customWidthValue,
            "data-tsid": tsid,
            ...restProps
        },
        ref,
    ) => {
        const { containerRef: buttonContainerRef } = useDropdownStore();

        const containerRef = useRef<HTMLDivElement | null>(null);

        /**
         * Устанавливаем maxHeight для контейнера
         */
        useIsomorphicLayoutEffect(() => {
            const container = containerRef.current;
            if (!container) {
                return;
            }

            const items = [
                ...container.querySelectorAll<HTMLElement>(
                    `[${DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ATRIBUTE}=${DROPDOWN_ITEM_HEIGHT_MEASURE_TARGET_ID}]`,
                ),
            ];

            const scrollHeight = calcScrollHeightByElementsCount(
                maxVisibleCells,
                items,
            );
            if (!scrollHeight) {
                return;
            }

            /**
             * Контейнеру необходимо установить корректную высоту ДО исполнения других useIsomorphicLayoutEffect:
             * например, в Popup.
             */
            container.style.maxHeight = `${scrollHeight}px`;
        }, [maxVisibleCells]);

        const dropdownInnerStyles = {
            ...(customWidthValue && {
                width: `${customWidthValue}px`,
            }),
            ...(width === "componentWidth" && {
                width: `${buttonContainerRef.current?.clientWidth}px`,
            }),
        };

        const dropdownWrapperClasses = cn(
            classes["dropdown-wrapper"],
            classes[`__view-${view}`],
            width !== "custom" &&
                width !== "componentWidth" && [classes[`__size-${width}`]],
            customColorsStyles,
            customVarsStyles,
        );

        const dropdownClasses = cn(classes["dropdown"], className);

        return (
            <div
                {...restProps}
                className={dropdownWrapperClasses}
                data-tsid={tsid}
                style={dropdownInnerStyles}
                ref={ref}
            >
                <div role={role} className={dropdownClasses} ref={containerRef}>
                    {children}
                </div>
            </div>
        );
    },
);

Base.displayName = "Menus.Base";

export default Base;
